import { useState } from 'react';

import { useAppDispatch } from '@verantos-dev/core-annotation-tool';

import { saveAnnotationAgentConcept, type AgentConcept } from '../../state/createProjectSlice';

// TODO: cleanup with cp_annotation_createproject_agent ff

export interface AgentConceptDraft {
  name: string;
  synonyms: string[];
  notes: string;
}

interface UseAgentConceptCardProps {
  conceptGroupId: string;
  concept: AgentConcept;
}

interface UseAgentConceptCardResult {
  isExpanded: boolean;
  draft: AgentConceptDraft | null;
  isNameBlank: boolean;
  handleToggleExpanded: () => void;
  handleEdit: () => void;
  handleDraftChange: (changes: Partial<AgentConceptDraft>) => void;
  handleSave: () => void;
  handleCancel: () => void;
}

/**
 * Local UI state of one concept card. Expanded state and the draft are never written to
 * Redux, so a card is collapsed again after the step is left and re-entered.
 */
export const useAgentConceptCard = ({
  conceptGroupId,
  concept,
}: UseAgentConceptCardProps): UseAgentConceptCardResult => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [draft, setDraft] = useState<AgentConceptDraft | null>(null);

  const isNameBlank = draft !== null && draft.name.trim() === '';

  // Collapsing only hides the card; an open draft survives until Save or Cancel.
  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleEdit = () => {
    setDraft({ name: concept.name, synonyms: concept.synonyms, notes: concept.notes });
    setIsExpanded(true);
  };

  const handleDraftChange = (changes: Partial<AgentConceptDraft>) => {
    setDraft((current) => (current ? { ...current, ...changes } : current));
  };

  const handleSave = () => {
    if (!draft || isNameBlank) return;
    dispatch(
      saveAnnotationAgentConcept({
        conceptGroupId,
        name: draft.name.trim(),
        synonyms: draft.synonyms,
        notes: draft.notes,
      })
    );
    setDraft(null);
    setIsExpanded(false);
  };

  const handleCancel = () => {
    setDraft(null);
  };

  return {
    isExpanded,
    draft,
    isNameBlank,
    handleToggleExpanded,
    handleEdit,
    handleDraftChange,
    handleSave,
    handleCancel,
  };
};
